/**
 * /booking — Inline Cal.com booking page for the user's purchased discovery
 * session. The coach/calendar comes from src/lib/coachMatch.ts — the SAME
 * assignment shown on the checkout gate and dashboard upsell:
 *
 *   currentSalary > $150k  → Linda Lutz, 60-min executive discovery ($67)
 *   else                   → Elisha Austin / Linda Leake, 30-min ($49), 50/50
 *
 * Uses the standard Cal.com embed.js snippet (no OAuth required — this embeds
 * a public calendar link). Pre-fills email + name from the Zustand quiz store
 * when available. After a successful booking fires `bookingSuccessfulV2`,
 * navigates to /dashboard.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuizStore } from '../store/quizStore';
import { matchCoach } from '../lib/coachMatch';

interface BookingTarget {
  /** Cal.com namespace — also used as a hash anchor in the script. */
  namespace: string;
  /** Cal.com link slug (the part after cal.com/). */
  link: string;
  /** Coach first name for header copy. */
  coachName: string;
  /** Session duration label for the lede. */
  durationLabel: string;
}

// Minimal Cal.com global type — the snippet creates a buffered command queue.
interface CalFn {
  (...args: unknown[]): void;
  loaded?: boolean;
  ns: Record<string, (...args: unknown[]) => void>;
  q: unknown[][];
}

declare global {
  interface Window {
    Cal?: CalFn;
  }
}

export function Booking() {
  const navigate = useNavigate();
  // Keep a ref so the booking callback never captures a stale navigate.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const leadEmail = useQuizStore((s) => s.leadEmail);
  const leadName = useQuizStore((s) => s.leadName);
  const leadId = useQuizStore((s) => s.leadId);
  const variables = useQuizStore((s) => s.variables);

  // Coach assignment comes from the shared matcher (same answer the checkout
  // gate and dashboard upsell showed), keyed on email so it's stable across
  // devices. Pinned via ref so the Cal.com init never sees a different target.
  const target = useMemo<BookingTarget>(() => {
    const m = matchCoach(variables, leadEmail ?? leadId ?? 'anon');
    return { namespace: m.calNamespace, link: m.calLink, coachName: m.firstName, durationLabel: m.durationLabel };
  }, [variables, leadEmail, leadId]);
  const targetRef = useRef(target);
  targetRef.current = target;

  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const { namespace, link } = targetRef.current;

    // ── Step 1: inject Cal.com snippet (sets up window.Cal command queue) ────
    // The snippet is synchronous — window.Cal is available immediately after.
    // embed.js is loaded lazily (appended to <head> inside the snippet).
    if (!window.Cal) {
      const snippet = document.createElement('script');
      // This is Cal.com's canonical embed snippet, verbatim.
      snippet.textContent = `
        (function (C, A, L) {
          let p = function (a, ar) { a.q.push(ar); };
          let d = C.document;
          C.Cal = C.Cal || function () {
            let cal = C.Cal; let ar = arguments;
            if (!cal.loaded) {
              cal.ns = {}; cal.q = cal.q || [];
              d.head.appendChild(d.createElement("script")).src = A;
              cal.loaded = true;
            }
            if (ar[0] === L) {
              const api = function () { p(api, arguments); };
              const namespace = ar[1];
              api.q = api.q || [];
              typeof namespace === "string" ? (cal.ns[namespace] = api) && p(api, ar) : p(cal, ar);
              return;
            }
            p(cal, ar);
          };
        })(window, "https://app.cal.com/embed/embed.js", "init");
      `;
      document.head.appendChild(snippet);
    }

    const Cal = window.Cal!;

    // ── Step 2: init namespace (creates Cal.ns[namespace] queue fn) ──────────
    Cal('init', namespace, { origin: 'https://cal.com' });

    // ── Step 3: mount inline booker with optional prefill ────────────────────
    const config: Record<string, unknown> = { layout: 'month_view' };
    if (leadEmail) config.email = leadEmail;
    if (leadName) config.name = leadName;

    Cal.ns[namespace]('inline', {
      elementOrSelector: '#cal-booking',
      config,
      calLink: link,
    });

    Cal.ns[namespace]('ui', {
      hideEventTypeDetails: false,
      layout: 'month_view',
    });

    // ── Step 4: redirect to dashboard after successful booking ────────────────
    Cal.ns[namespace]('on', {
      action: 'bookingSuccessfulV2',
      callback: () => {
        // Small delay so the user sees Cal.com's confirmation screen briefly.
        window.setTimeout(() => {
          navigateRef.current('/dashboard');
        }, 2000);
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — intentionally once

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="max-w-4xl mx-auto w-full px-4 py-10 sm:py-14">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-8 sm:mb-10 text-center">
          <p className="eyebrow mb-2">1:1 session</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-ink leading-tight mb-3">
            Book your session with {target.coachName}
          </h1>
          <p className="text-ink-soft text-base max-w-md mx-auto leading-relaxed">
            {target.durationLabel}, 1:1, with a career coach. Pick a time that
            works — your details are already filled in below.
          </p>
        </div>

        {/* ── Cal.com embed target ────────────────────────────────────────── */}
        {/*
          Cal.com injects an <iframe> into this div. min-height prevents layout
          jump; overflow:scroll matches Cal's recommended inline embed style.
        */}
        <div
          id="cal-booking"
          className="rounded-3xl overflow-hidden border-2 border-ink/10 bg-white"
          style={{ minHeight: 680, overflowY: 'scroll' }}
        />

        {/* ── Back link ───────────────────────────────────────────────────── */}
        <p className="text-center mt-6 text-sm text-ink-soft">
          <button
            type="button"
            className="underline hover:text-ink transition"
            onClick={() => navigateRef.current('/dashboard')}
          >
            ← Back to my dashboard
          </button>
        </p>

      </div>
    </div>
  );
}
